/**
 * Технічні дані звернення «Повідомити про проблему» (UX-51) БЕЗ персональних даних (SEC-110,
 * SEC-165). Принцип: не «зібрати все й прибрати зайве», а список дозволених полів, кожне з яких
 * обчислюється з відомого джерела і перевіряється за формою перед тим, як потрапити у звернення.
 * Нового поля тут не буде без рішення Security: звернення йде поза систему (лист, форма
 * підтримки), а розробка ведеться без доступу до реальних ПД.
 *
 * Що входить: збірка (хеш файла застосунку), браузер і ОС (назва й мажорна версія, без рядка
 * User-Agent), розмір вікна, легкий режим, стан мережі, ШАБЛОН маршруту (`/l/$entity/$id`, без
 * значень параметрів, query і фрагмента), останні 10 кодів помилок з `traceId` і часом.
 * Чого немає: адреси сторінки, назв записів, значень полів і фільтрів, тіл запитів і відповідей,
 * тексту помилок сервера, стеків, імені чи email людини, знімка екрана.
 */
import { cleanCode, cleanTraceId, supportCode, traceIdOf } from './classify.ts';

// Журнал (цей файл) — у головному чанку: помилки треба запам'ятовувати з першої секунди. Збір і текст
// технічних даних — у `diagnostics-collect.ts`, лінивому разом із формою звернення.

export const MAX_RECENT_ERRORS = 10;

export interface RecentError {
  code: string;
  traceId: string | null;
  /** ISO 8601, UTC: за часом підтримка знаходить запит у журналах. */
  at: string;
}

/** Невідоме значення: у тексті звернення читається, а не лишається порожнім рядком. */
export const UNKNOWN = 'невідомо';

// ---------------------------------------------------------------- допустимі форми значень

/** Хеш у назві файла збірки (`index-AbC123_x.js` → `AbC123_x`) або `dev`. */
export const BUILD_SHAPE = /^(dev|[A-Za-z0-9_-]{6,16})$/;
/** Шаблон маршруту роутера: сегменти з `$param`, без `?`, `#`, `=`, `%`, пробілів. */
export const ROUTE_SHAPE = /^\/?[A-Za-z0-9_$./-]{0,119}$/;

export const onlyIf = <T extends string>(
  value: string,
  shape: RegExp,
  fallback: T | string,
): string => (shape.test(value) ? value : fallback);

// ---------------------------------------------------------------- збірка

let build = 'dev';

/**
 * Ідентифікатор збірки з адреси головного файла (`import.meta.url` у main.tsx): хеш у назві
 * змінюється разом із вмістом, тож за ним видно, яку версію бачила людина. У dev адреса
 * `/src/main.tsx` — тоді `dev`. Коли збірку зроблено з `BUILD_SHA` (образ), береться він із meta-тега, див.
 * `buildIdFromSha`; хеш файла — запасний варіант локальної збірки.
 */
export function buildIdFromUrl(url: string): string {
  const match = /\/assets\/[A-Za-z0-9_.-]*?-([A-Za-z0-9_-]{6,16})\.js(?:$|\?)/.exec(url);
  return match?.[1] ? onlyIf(match[1], BUILD_SHAPE, 'dev') : 'dev';
}

/**
 * Git-коміт збірки (`VITE_BUILD_SHA` ← `BUILD_SHA` образу): перші 12 символів hex. `dev` / `local`
 * (локальна збірка без аргументу) і все, що не схоже на sha, — `null`, тоді береться хеш файла.
 */
export function buildIdFromSha(sha: string | undefined): string | null {
  const short = /^[0-9a-f]{12,64}$/.test(sha ?? '') ? (sha as string).slice(0, 12) : null;
  return short && BUILD_SHAPE.test(short) ? short : null;
}

/** Ідентифікатор збірки для технічних даних звернення. */
export const currentBuild = (): string => build;

/**
 * Sha збірки з `<meta name="build">` в index.html (Vite підставляє `%VITE_BUILD_SHA%` у HTML, а не в
 * JS: інакше імена чанків залежали б від коміту). Немає тега — `undefined`; значення повертається як є (літерал `%…%` чи `dev` відкидає `buildIdFromSha`).
 */
export function buildShaFromMeta(doc: Document = document): string | undefined {
  return doc.querySelector<HTMLMetaElement>('meta[name="build"]')?.content || undefined;
}

export function registerBuild(url: string, sha: string | undefined = buildShaFromMeta()): void {
  build = buildIdFromSha(sha) ?? buildIdFromUrl(url);
}

// ---------------------------------------------------------------- маршрут

let routeSource: (() => string | null) | null = null;

/** Роутер повідомляє, як дізнатись шаблон поточного маршруту (`createAppRouter`). */
export function registerRouteSource(source: () => string | null): void {
  routeSource = source;
}

/** Шаблон маршруту (`/l/$entity/$id`) чи `UNKNOWN`; значень параметрів тут не буває за побудовою. */
export function currentRoute(): string {
  let pattern: string | null = null;
  try {
    pattern = routeSource?.() ?? null;
  } catch {
    pattern = null;
  }
  if (!pattern || pattern === '__root__') return UNKNOWN;
  const trimmed = pattern.length > 1 ? pattern.replace(/\/$/, '') : pattern;
  return onlyIf(trimmed, ROUTE_SHAPE, UNKNOWN);
}

// ---------------------------------------------------------------- останні помилки

const recent: RecentError[] = [];
const listeners = new Set<() => void>();

/**
 * Запам'ятати показану людині помилку: лише код і `traceId` (обидва перевіряються за формою),
 * час — з годинника браузера. Однакова пара підряд не дублюється (повторний рендер стану).
 */
export function noteError(code: string, traceId: string | null = null, at = new Date()): void {
  if (!cleanCode(code)) return;
  const trace = cleanTraceId(traceId);
  const last = recent[recent.length - 1];
  if (last && last.code === code && last.traceId === trace) return;
  recent.push({ code, traceId: trace, at: at.toISOString() });
  if (recent.length > MAX_RECENT_ERRORS) recent.splice(0, recent.length - MAX_RECENT_ERRORS);
  for (const listener of listeners) listener();
}

/**
 * Записати збій запиту чи зміни. Скасований запит (`AbortError`) і 401 (сесії немає: це не збій, а
 * перехід на вхід) у журнал не йдуть. З помилки береться лише код і `traceId`.
 */
export function noteFailure(error: unknown): void {
  if ((error as { name?: unknown } | null)?.name === 'AbortError') return;
  if ((error as { status?: unknown } | null)?.status === 401) return;
  noteError(supportCode(error), traceIdOf(error));
}

export function recentErrors(): RecentError[] {
  return recent.map((e) => ({ ...e }));
}

export function lastError(): RecentError | null {
  const last = recent[recent.length - 1];
  return last ? { ...last } : null;
}

export function resetRecentErrors(): void {
  recent.length = 0;
}
